"use client";

import { SaveFiltersButton } from "@/app/(saas)/(app)/businesses/_components/buttons/save-filters-button";
import { LocationSearchBox } from "@/app/(saas)/(app)/businesses/_components/filters/location-search-box";
import { BusinessTypeFilterPopover } from "./filters/popovers/type-filter-popover";
import { PriceRangeFilterPopover } from "./filters/popovers/price-range-filter-popover";
import { MoreFiltersPopover } from "./filters/popovers/more-filters-popover";
import { PriceChangeFilterPopover } from "./filters/popovers/price-change-filter-popover";
import { businessFiltersAtom } from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { BusinessListSort } from "@/app/(saas)/(app)/businesses/_components/business-list-sort";
import type { SearchData } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useAtom } from "jotai";
import { useMemo } from "react";

export function BusinessFilters({
  getSearchData,
  sortOptions,
  sortValue,
  onSortValueChange,
}: {
  getSearchData: () => Promise<SearchData | null>;
  sortOptions?: readonly string[];
  sortValue?: string;
  onSortValueChange?: (value: string) => void;
}) {
  const [filters, setFilters] = useAtom(businessFiltersAtom);

  const totalActiveFilters = useMemo(() => {
    let count = 0;
    count += filters.businessTypeIds.length;
    count += filters.statuses.length;
    count += filters.listedBy.length;
    if (filters.priceRange.min !== null || filters.priceRange.max !== null)
      count++;
    if (filters.revenueRange.min !== null || filters.revenueRange.max !== null)
      count++;
    if (filters.profitRange.min !== null || filters.profitRange.max !== null)
      count++;
    if (filters.sdeRange.min !== null || filters.sdeRange.max !== null) count++;
    if (
      filters.sdeMultipleRange.min !== null ||
      filters.sdeMultipleRange.max !== null
    )
      count++;
    if (
      filters.revenueMultipleRange.min !== null ||
      filters.revenueMultipleRange.max !== null
    )
      count++;
    if (
      filters.downPaymentRange.min !== null ||
      filters.downPaymentRange.max !== null
    )
      count++;
    if (!filters.includeMissingMultiples) count++;
    if (
      filters.establishmentAgeRange.min !== null ||
      filters.establishmentAgeRange.max !== null
    )
      count++;
    if (
      filters.employeeCountRange.min !== null ||
      filters.employeeCountRange.max !== null
    )
      count++;
    if (
      filters.ownerWorkedHoursRange.min !== null ||
      filters.ownerWorkedHoursRange.max !== null
    )
      count++;
    if (filters.propertyIncluded) count++;
    if (filters.financingAvailable) count++;
    if (filters.sbaPrequalified) count++;
    if (filters.visaQualified) count++;
    if (filters.priceDirection) count++;
    if (filters.priceChangedWithinDays) count++;
    if (filters.minPriceChangePercent) count++;
    return count;
  }, [filters]);

  function clearAllFilters() {
    setFilters((prev) => ({
      ...prev,
      businessTypeIds: [],
      statuses: [],
      listedBy: [],
      priceRange: { min: null, max: null },
      revenueRange: { min: null, max: null },
      profitRange: { min: null, max: null },
      sdeRange: { min: null, max: null },
      sdeMultipleRange: { min: null, max: null },
      revenueMultipleRange: { min: null, max: null },
      downPaymentRange: { min: null, max: null },
      establishmentAgeRange: { min: null, max: null },
      employeeCountRange: { min: null, max: null },
      ownerWorkedHoursRange: { min: null, max: null },
      propertyIncluded: null,
      financingAvailable: null,
      sbaPrequalified: null,
      visaQualified: null,
      includeMissingMultiples: true,
      priceDirection: null,
      priceChangedWithinDays: null,
      minPriceChangePercent: null,
    }));
  }

  return (
    <div
      className={cn(
        "relative z-20 hidden bg-transparent px-[18px] py-3 xl:flex",
        totalActiveFilters === 0 && "border-b border-border/70",
      )}
    >
      <div className="flex w-full items-start gap-3 rounded-xl border border-border/80 bg-card/90 px-3 py-2 shadow-[0_1px_0_rgba(255,255,255,0.92)_inset,0_8px_24px_rgba(0,0,0,0.08)] backdrop-blur-md supports-[backdrop-filter]:bg-card/72">
        <LocationSearchBox />

        <div className="mt-1.5 h-8 w-px bg-muted" />

        <BusinessTypeFilterPopover />
        <PriceRangeFilterPopover />
        <PriceChangeFilterPopover />
        <MoreFiltersPopover />

        {totalActiveFilters > 0 && (
          <button
            type="button"
            onClick={clearAllFilters}
            className="whitespace-nowrap text-sm text-muted-foreground transition-colors hover:text-foreground hover:underline"
          >
            Clear all
          </button>
        )}

        <div className="flex-1" />

        {sortOptions && sortValue && onSortValueChange && (
          <BusinessListSort
            options={sortOptions}
            value={sortValue}
            onChange={onSortValueChange}
          />
        )}

        <SaveFiltersButton getSearchData={getSearchData} />
      </div>
    </div>
  );
}

BusinessFilters.displayName = "BusinessFilters";
